import { useEffect } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import * as turf from '@turf/turf';
import { lv95ToWgs84ForOneCoordinate, wgs84ToLv95ForOneCoordinate } from '../map/coordinateTransforms';
import { getCombinedAnalysisAreasCenter } from './cadastralGeometry';
import { getLayerMap, type Lv95Coordinate, type SetGridParams, type TurfPolygon } from './cadastralShared';
import type { GridMapType } from '../map/mapConfig';

export const createCenterMarkerIcon = () => {
  const markerSize = 16;
  return L.divIcon({
    className: 'custom-center-marker',
    iconSize: [markerSize, markerSize],
    iconAnchor: [markerSize / 2, markerSize / 2],
    html: `<div style="width:${markerSize}px;height:${markerSize}px;line-height:${markerSize}px;text-align:center;font-size:14px;color:#e53935;">✖</div>`,
  });
};

export const useAnalysisAreaCenterMarker =
  (centerOfAnalysisAreaRef: { current: L.Marker | null }, onCenterUpdated: () => void) =>
  (
    polygon: TurfPolygon,
    map: L.Map | undefined,
    setGridParams: SetGridParams,
    centerOverride: [number, number] | null = null,
    options: { updateGridParams?: boolean } = {}
  ) => {
    if (!map) {
      return;
    }
    const { updateGridParams = true } = options;
    const polygonCenter = turf.center(polygon).geometry.coordinates;
    const center = centerOverride
      ? (() => {
          const { lng, lat } = lv95ToWgs84ForOneCoordinate(centerOverride[0], centerOverride[1]);
          return [lng, lat];
        })()
      : polygonCenter;
    if (!Array.isArray(center) || center.length !== 2 || !Number.isFinite(center[0]) || !Number.isFinite(center[1])) {
      return;
    }
    if (updateGridParams) {
      const lv95Center = wgs84ToLv95ForOneCoordinate(center[0], center[1]);
      setGridParams({
        manualCenter: {
          x: lv95Center.x,
          y: lv95Center.y,
        },
      });
    }

    const markerExists = centerOfAnalysisAreaRef.current;
    const markerOnSameMap = markerExists && getLayerMap(markerExists) === map;

    if (markerExists && !markerOnSameMap) {
      getLayerMap(markerExists)?.removeLayer(markerExists);
    }

    if (!markerExists || !markerOnSameMap) {
      const marker = L.marker([center[1], center[0]], {
        icon: createCenterMarkerIcon(),
        draggable: true,
      }).addTo(map);
      centerOfAnalysisAreaRef.current = marker;

      marker.on('drag', function (event) {
        const markerCoords = (event.target as L.Marker).getLatLng();
        const updatedLv95Coords = wgs84ToLv95ForOneCoordinate(markerCoords.lng, markerCoords.lat);

        setGridParams({
          manualCenter: {
            x: updatedLv95Coords.x,
            y: updatedLv95Coords.y,
          },
        });
      });

      marker.on('dragend', function (event) {
        const markerCoords = (event.target as L.Marker).getLatLng();
        const updatedLv95Coords = wgs84ToLv95ForOneCoordinate(markerCoords.lng, markerCoords.lat);

        setGridParams({
          manualCenter: {
            x: updatedLv95Coords.x,
            y: updatedLv95Coords.y,
          },
        });
        onCenterUpdated();
      });
    } else {
      const currentLatLng = markerExists.getLatLng();
      if (currentLatLng.lat !== center[1] || currentLatLng.lng !== center[0]) {
        markerExists.setLatLng([center[1], center[0]]);
      }
    }
  };

interface AnalysisAreaCenterMarkerSyncProps {
  analysisArea: Lv95Coordinate[];
  analysisAreas: Lv95Coordinate[][];
  manualCenter: Lv95Coordinate | null;
  mapType: GridMapType;
  readOnlyMode: boolean;
  setGridParams: SetGridParams;
  centerOfAnalysisAreaRef: { current: L.Marker | null };
  reviewCenterMarkersRef: { current: L.Marker[] };
  addCenterMarker: AddCenterMarker;
}

export type AddCenterMarker = (
  polygon: TurfPolygon,
  map: L.Map | undefined,
  setGridParams: SetGridParams,
  centerOverride?: [number, number] | null,
  options?: { updateGridParams?: boolean }
) => void;

const AnalysisAreaCenterMarkerSync = ({
  analysisArea,
  analysisAreas,
  manualCenter,
  mapType,
  readOnlyMode,
  setGridParams,
  centerOfAnalysisAreaRef,
  reviewCenterMarkersRef,
  addCenterMarker,
}: AnalysisAreaCenterMarkerSyncProps) => {
  const map = useMap();

  useEffect(() => {
    reviewCenterMarkersRef.current.forEach((marker) => {
      getLayerMap(marker)?.removeLayer(marker);
    });
    reviewCenterMarkersRef.current = [];

    if (readOnlyMode) {
      const manualCenterX = manualCenter?.x;
      const manualCenterY = manualCenter?.y;
      const hasManualCenter =
        Number.isFinite(manualCenterX) &&
        Number.isFinite(manualCenterY) &&
        manualCenterX != null &&
        manualCenterY != null &&
        (manualCenterX !== 0 || manualCenterY !== 0);
      const center = hasManualCenter
        ? { x: Number(manualCenterX), y: Number(manualCenterY) }
        : getCombinedAnalysisAreasCenter(analysisAreas);
      if (center) {
        const markerCoords = lv95ToWgs84ForOneCoordinate(center.x, center.y);
        const marker = L.marker([markerCoords.lat, markerCoords.lng], {
          icon: createCenterMarkerIcon(),
          draggable: false,
          interactive: false,
        }).addTo(map);

        reviewCenterMarkersRef.current.push(marker);
      }

      const existingMarker = centerOfAnalysisAreaRef.current;
      if (existingMarker) {
        getLayerMap(existingMarker)?.removeLayer(existingMarker);
        centerOfAnalysisAreaRef.current = null;
      }
      return () => {
        reviewCenterMarkersRef.current.forEach((marker) => {
          getLayerMap(marker)?.removeLayer(marker);
        });
        reviewCenterMarkersRef.current = [];
      };
    }

    if (!analysisArea || analysisArea.length < 3) {
      const existingMarker = centerOfAnalysisAreaRef.current;
      if (existingMarker) {
        getLayerMap(existingMarker)?.removeLayer(existingMarker);
        centerOfAnalysisAreaRef.current = null;
      }
      return;
    }
    const wgs84Coordinates = analysisArea.map((coordinate) => lv95ToWgs84ForOneCoordinate(coordinate.x, coordinate.y));
    const polygonCoordinates = wgs84Coordinates.map((coord) => [coord.lng, coord.lat]);
    polygonCoordinates.push(polygonCoordinates[0]);
    const polygon = turf.polygon([polygonCoordinates]);
    const manualCenterX = manualCenter?.x;
    const manualCenterY = manualCenter?.y;
    const hasManualCenter =
      Number.isFinite(manualCenterX) &&
      Number.isFinite(manualCenterY) &&
      manualCenterX != null &&
      manualCenterY != null &&
      (manualCenterX !== 0 || manualCenterY !== 0);
    const centerOverride: [number, number] | null = hasManualCenter ? [manualCenterX, manualCenterY] : null;
    addCenterMarker(polygon, map, setGridParams, centerOverride, { updateGridParams: !hasManualCenter });
    return () => {
      reviewCenterMarkersRef.current.forEach((marker) => {
        getLayerMap(marker)?.removeLayer(marker);
      });
      reviewCenterMarkersRef.current = [];
    };
  }, [getCombinedAnalysisAreasCenter, map, mapType, analysisArea, analysisAreas, manualCenter, readOnlyMode]);

  return null;
};

export default AnalysisAreaCenterMarkerSync;
